"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const material_1 = require("@mui/material");
const EditOutlined_1 = __importDefault(require("@mui/icons-material/EditOutlined"));
const react_rnd_1 = require("react-rnd");
const ImageEditor_1 = __importDefault(require("../ImageEditor"));
const SvgShapeRenderer_1 = __importDefault(require("../ShapeEditor/SvgShapeRenderer"));
const Table_1 = __importDefault(require("../Table"));
const TextBox_1 = __importDefault(require("../TextBox"));
const componentMap = {
    CustomTableEditor: Table_1.default,
    CustomTextEditor: TextBox_1.default,
    CustomImageEditor: ImageEditor_1.default,
    ShapeEditor: SvgShapeRenderer_1.default, // Use the SVG shape renderer
};
const SlideEditor = ({ config = [], onShapeClick }) => {
    const [editorValue, setEditorValue] = (0, react_1.useState)({});
    const [hoveredKey, setHoveredKey] = (0, react_1.useState)(null);
    const [selectedKey, setSelectedKey] = (0, react_1.useState)(null);
    const containerRef = (0, react_1.useRef)(null);
    const handleValueChange = (0, react_1.useCallback)((key, newValue) => {
        setEditorValue((prev) => (Object.assign(Object.assign({}, prev), { [key]: newValue })));
    }, []);
    const handleElementClick = (0, react_1.useCallback)((e, uniqueKey) => {
        e.stopPropagation();
        setSelectedKey(uniqueKey);
    }, []);
    const handleSlideClick = (0, react_1.useCallback)(() => {
        setSelectedKey(null);
    }, []);
    return ((0, jsx_runtime_1.jsx)("div", { ref: containerRef, style: {
            width: "100%",
            background: "#f5f5f5",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            boxSizing: "border-box",
        }, children: config.map((slide) => {
            var _a;
            return ((0, jsx_runtime_1.jsx)("div", { onClick: handleSlideClick, style: {
                    width: "100%",
                    maxWidth: "1778px", // Cap max width
                    aspectRatio: "16 / 9", // Maintain 16:9 ratio
                    position: "relative",
                    backgroundColor: "#ffffff",
                    fontFamily: "Arial, sans-serif",
                    fontSize: "16px",
                    color: "#000000",
                    border: "1px solid #ccc",
                    marginBottom: "40px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    overflow: "hidden",
                }, children: (_a = slide === null || slide === void 0 ? void 0 : slide.components) === null || _a === void 0 ? void 0 : _a.map((item, compIndex) => {
                    const Comp = componentMap[item.component];
                    const uniqueKey = `${slide.id}-${compIndex}`;
                    if (!Comp) {
                        console.warn(`Component "${item.component}" not found in componentMap`);
                        return null;
                    }
                    // Memoize the default values to prevent unnecessary re-renders
                    const defaultValues = (0, react_1.useMemo)(() => ({
                        x: item.props.initialX || item.props.defaultX || item.props.x || 0,
                        y: item.props.initialY || item.props.defaultY || item.props.y || 0,
                        width: item.props.initialWidth || item.props.editorWidth || item.props.width || 300,
                        height: item.props.initialHeight || item.props.editorHeight || item.props.height || 150,
                    }), [item.props.initialX, item.props.defaultX, item.props.x, item.props.initialY, item.props.defaultY, item.props.y, item.props.initialWidth, item.props.editorWidth, item.props.width, item.props.initialHeight, item.props.editorHeight, item.props.height]);
                    return ((0, jsx_runtime_1.jsx)(react_rnd_1.Rnd, { default: defaultValues, minWidth: 50, minHeight: 50, enableResizing: selectedKey === uniqueKey && item.props.isResizable !== false, disableDragging: item.props.isDraggable === false, resizeHandleStyles: {
                            bottom: {
                                bottom: "-5px",
                                height: "10px",
                                width: "100%",
                                cursor: "row-resize",
                                backgroundColor: "rgba(0, 120, 255, 0.3)",
                            },
                            bottomLeft: {
                                bottom: "-10px",
                                left: "-10px",
                                width: "20px",
                                height: "20px",
                                cursor: "sw-resize",
                                backgroundColor: "rgba(0, 120, 255, 0.5)",
                                borderRadius: "50%",
                            },
                            bottomRight: {
                                bottom: "-10px",
                                right: "-10px",
                                width: "20px",
                                height: "20px",
                                cursor: "se-resize",
                                backgroundColor: "rgba(0, 120, 255, 0.5)",
                                borderRadius: "50%",
                            },
                            left: {
                                left: "-5px",
                                width: "10px",
                                height: "100%",
                                cursor: "col-resize",
                                backgroundColor: "rgba(0, 120, 255, 0.3)",
                            },
                            right: {
                                right: "-5px",
                                width: "10px",
                                height: "100%",
                                cursor: "col-resize",
                                backgroundColor: "rgba(0, 120, 255, 0.3)",
                            },
                            top: {
                                top: "-5px",
                                height: "10px",
                                width: "100%",
                                cursor: "row-resize",
                                backgroundColor: "rgba(0, 120, 255, 0.3)",
                            },
                            topLeft: {
                                top: "-10px",
                                left: "-10px",
                                width: "20px",
                                height: "20px",
                                cursor: "nw-resize",
                                backgroundColor: "rgba(0, 120, 255, 0.5)",
                                borderRadius: "50%",
                            },
                            topRight: {
                                top: "-10px",
                                right: "-10px",
                                width: "20px",
                                height: "20px",
                                cursor: "ne-resize",
                                backgroundColor: "rgba(0, 120, 255, 0.5)",
                                borderRadius: "50%",
                            },
                        }, style: {
                            // Keep shapes interactive by ensuring they are not behind other components
                            zIndex: item.component === "ShapeEditor" ? 10 : 10,
                            border: selectedKey === uniqueKey ? "2px solid #0078ff" : "none",
                            borderRadius: "4px",
                        }, bounds: "parent", onClick: (e) => handleElementClick(e, uniqueKey), onDragStop: (0, react_1.useCallback)((e, d) => {
                            if (item.component === "ShapeEditor") {
                                // Handle shape drag updates
                                // TODO: Update shape position in state
                            }
                        }, [item.component]), onResizeStop: (0, react_1.useCallback)((e, direction, ref, delta, position) => {
                            if (item.component === "ShapeEditor") {
                                // Handle shape resize updates
                                // TODO: Update shape size in state
                            }
                        }, [item.component]), children: (0, jsx_runtime_1.jsxs)("div", { style: { position: "relative", width: "100%", height: "100%" }, onMouseEnter: () => setHoveredKey(uniqueKey), onMouseLeave: () => setHoveredKey((prev) => (prev === uniqueKey ? null : prev)), children: [(0, jsx_runtime_1.jsx)(Comp, Object.assign({}, item.props, { editorValue: editorValue[uniqueKey] || "", setEditorValue: (val) => handleValueChange(uniqueKey, val), hideToolbar: item.component === "ShapeEditor" ? true : undefined })), item.component === "ShapeEditor" && onShapeClick && selectedKey === uniqueKey && ((0, jsx_runtime_1.jsx)(material_1.IconButton, { size: "small", onClick: (e) => {
                                        e.stopPropagation();
                                        onShapeClick(item, slide.id);
                                    }, sx: {
                                        position: "absolute",
                                        top: 8,
                                        left: 8, // Move to left to avoid resize handles
                                        bgcolor: "rgba(255,255,255,0.9)",
                                        border: "1px solid #e0e0e0",
                                        boxShadow: 1,
                                        p: 0.5,
                                        zIndex: 1, // Lower than resize handles
                                        pointerEvents: "auto", // Allow clicking the icon
                                        '&:hover': { bgcolor: "#ffffff" },
                                    }, children: (0, jsx_runtime_1.jsx)(EditOutlined_1.default, { fontSize: "small" }) }))] }) }, uniqueKey));
                }) }, slide.id));
        }) }));
};
exports.default = SlideEditor;
